/*
 * ============================================================
 * SAV_ERP — Smart Account Vision Enterprise Resource Planning
 * Copyright (c) 2026 Hatem Mohamed Abdalla.
 * All rights reserved.
 *
 * This file is part of a proprietary software product owned
 * solely by Hatem Mohamed Abdalla (UAE, +971 50 390 9781).
 * Copying, redistribution, modification, reverse engineering
 * or reuse in any form without prior written consent of the
 * copyright owner is strictly prohibited and will be
 * prosecuted under UAE Federal Law No. 38/2021 on Copyright
 * and applicable international treaties.
 *
 * Fingerprint: SAV-ERP-HMA-2026-#a7f3d0
 * ============================================================
 */
import { useCallback, useEffect, useState } from 'react';

/**
 * useActiveBranch — Phase 1c.
 *
 * Owns the "active branch" state per (user, company). Persisted in
 * localStorage so a reload keeps context. Exposes:
 *   * mode          : 'OFF' | 'ACTIVE' | 'FROZEN' | null   (null while loading)
 *   * branches      : list of branch documents
 *   * activeBranchId: currently-selected branch id (or 'ALL' for view-only)
 *   * isViewingAll  : true when activeBranchId === 'ALL'
 *   * setActiveBranch(id): update the active branch (persists to localStorage)
 *   * reload()      : refresh both feature + branches state
 *
 * When mode === 'OFF' the hook returns branches=[] and a null activeBranchId
 * so callers can render exactly the pre-branch behaviour.
 *
 * The default active branch is the MAIN branch when we can find one.
 * 'ALL' is only selectable, never the default, and creation forms must
 * refuse to submit while active === 'ALL'.
 */
const STORAGE_PREFIX = 'sav_active_branch__';

export function useActiveBranch({ companyCode, apiFetch }) {
  const storageKey = companyCode ? `${STORAGE_PREFIX}${companyCode}` : null;
  const [feature, setFeature] = useState(null);
  const [branches, setBranches] = useState([]);
  const [activeBranchId, setActiveBranchIdState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const persist = useCallback((value) => {
    if (!storageKey) return;
    try {
      if (value) localStorage.setItem(storageKey, value);
      else localStorage.removeItem(storageKey);
    } catch (_) { /* ignore quota errors */ }
  }, [storageKey]);

  const setActiveBranch = useCallback((id) => {
    setActiveBranchIdState(id || null);
    persist(id || null);
  }, [persist]);

  const reload = useCallback(async () => {
    if (!companyCode || !apiFetch) {
      setLoading(false);
      return;
    }
    setLoading(true); setError('');
    try {
      const [featResp, branchesResp] = await Promise.all([
        apiFetch(`/api/companies/${companyCode}/features/branch-management`),
        apiFetch(`/api/companies/${companyCode}/branches?include_inactive=false`)
          .catch(() => ({ items: [] })),
      ]);
      setFeature(featResp);
      const items = Array.isArray(branchesResp?.items) ? branchesResp.items : [];
      setBranches(items);

      if ((featResp?.mode || 'OFF') === 'OFF') {
        setActiveBranchIdState(null);
        persist(null);
      } else {
        let saved = null;
        try { saved = storageKey ? localStorage.getItem(storageKey) : null; }
        catch (_) { saved = null; }
        const stillExists = saved && (
          saved === 'ALL' || items.some((b) => b.id === saved)
        );
        if (stillExists) {
          setActiveBranchIdState(saved);
        } else {
          const main = items.find((b) => b.is_main);
          setActiveBranchIdState(main ? main.id : (items[0]?.id || null));
        }
      }
    } catch (e) {
      setError(e?.message || 'تعذر تحميل بيانات الفروع.');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, companyCode, persist, storageKey]);

  useEffect(() => { reload(); }, [reload]);

  return {
    mode: feature?.mode || null,
    branches,
    activeBranchId,
    isViewingAll: activeBranchId === 'ALL',
    setActiveBranch,
    reload,
    loading,
    error,
  };
}

export default useActiveBranch;
